import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card, Button, Input, Select, Spinner, Empty, Badge, fmtBRL } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Plus, Pencil, UserX, Trash2, X } from "lucide-react";

const emptyForm = {
  nome: "", cpf: "", rg: "", ctps: "", pis: "",
  cargo: "", unidade: "Matriz VGS", tipo_contrato: "CLT",
  salario_base: "", complemento_mensal: "", valor_diaria: "", valor_hora: "", valor_hora_extra: "", dependentes: 0,
  salario_parcelado: true, dia_adiantamento: 16,
  vale_transporte: "", vale_alimentacao: "",
  email: "", telefone: "", chave_pix: "",
  data_admissao: "", vinculado_vendedor: "", observacoes: "",
};

export default function Funcionarios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [modal, setModal] = useState(null); // {mode:"new"|"edit", data:{}}
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = showAll ? {} : { status: "ativo" };
      const { data } = await api.get("/rh/funcionarios", { params });
      setItems(data || []);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showAll]);

  const openNew = () => { setForm(emptyForm); setModal({ mode: "new" }); setErr(""); };
  const openEdit = (f) => {
    setForm({
      ...emptyForm, ...f,
      salario_base: f.salario_base ?? "",
      complemento_mensal: f.complemento_mensal ?? "",
      valor_diaria: f.valor_diaria ?? "",
      valor_hora: f.valor_hora ?? "",
      valor_hora_extra: f.valor_hora_extra ?? "",
      vale_transporte: f.vale_transporte ?? "",
      vale_alimentacao: f.vale_alimentacao ?? "",
    });
    setModal({ mode: "edit", data: f });
    setErr("");
  };
  const close = () => { setModal(null); setForm(emptyForm); setErr(""); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        salario_base: Number(form.salario_base) || 0,
        complemento_mensal: Number(form.complemento_mensal) || 0,
        valor_diaria: Number(form.valor_diaria) || 0,
        valor_hora: Number(form.valor_hora) || 0,
        valor_hora_extra: Number(form.valor_hora_extra) || 0,
        vale_transporte: Number(form.vale_transporte) || 0,
        vale_alimentacao: Number(form.vale_alimentacao) || 0,
        dependentes: Number(form.dependentes) || 0,
        salario_parcelado: !!form.salario_parcelado,
        dia_adiantamento: Math.min(28, Math.max(1, Number(form.dia_adiantamento) || 16)),
        inss_empresa_pct: Math.min(100, Math.max(0, Number(form.inss_empresa_pct) || 0)),
      };
      if (modal.mode === "new") await api.post("/rh/funcionarios", payload);
      else await api.put(`/rh/funcionarios/${modal.data.id}`, payload);
      close(); await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const desligar = async (f) => {
    if (!window.confirm(`Desligar ${f.nome}?`)) return;
    try { await api.post(`/rh/funcionarios/${f.id}/desligar`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Excluir ${f.nome} permanentemente?\nSó funciona se ele nunca teve folha.`)) return;
    try { await api.delete(`/rh/funcionarios/${f.id}`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Layout
      title="Funcionários"
      subtitle="Cadastro de colaboradores por unidade"
      actions={
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-xs text-ink-500 cursor-pointer">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="funcionarios-show-all" />
            mostrar desligados
          </label>
          <Button onClick={openNew} data-testid="new-funcionario-btn"><Plus size={16} /> Novo</Button>
        </div>
      }
    >
      {loading ? <div className="flex justify-center py-20"><Spinner size={28} /></div> : items.length === 0 ? (
        <Card><Empty title="Nenhum funcionário cadastrado" hint="Clique em 'Novo' para começar." /></Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm" data-testid="funcionarios-table">
            <thead className="bg-stone-50 border-b border-ink-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Cargo</th>
                <th className="px-4 py-3">Unidade</th>
                <th className="px-4 py-3">Contrato</th>
                <th className="px-4 py-3 text-right">Salário / Diária</th>
                <th className="px-4 py-3">Vinc. Vendedor</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 w-1">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id} className="border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-ink-900">{f.nome}</td>
                  <td className="px-4 py-3 text-ink-700">{f.cargo || "—"}</td>
                  <td className="px-4 py-3 text-ink-700">{f.unidade}</td>
                  <td className="px-4 py-3"><Badge tone="neutral">{f.tipo_contrato}</Badge></td>
                  <td className="px-4 py-3 font-mono text-right">
                    {f.tipo_contrato === "Diarista" ? (
                      `${fmtBRL(f.valor_diaria)}/dia`
                    ) : (
                      <>
                        <div>{fmtBRL((Number(f.salario_base) || 0) + (Number(f.complemento_mensal) || 0))}</div>
                        {Number(f.complemento_mensal) > 0 && (
                          <div className="text-[10px] text-ink-500 leading-tight font-normal">
                            CTPS {fmtBRL(f.salario_base)} + compl {fmtBRL(f.complemento_mensal)}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-700">{f.vinculado_vendedor || "—"}</td>
                  <td className="px-4 py-3">
                    <Badge tone={f.status === "ativo" ? "success" : "danger"}>{f.status}</Badge>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button onClick={() => openEdit(f)} className="p-1.5 rounded-md hover:bg-emerald-100 text-brand-500" title="Editar" data-testid={`func-edit-${f.id}`}><Pencil size={16} /></button>
                    {f.status === "ativo" && <button onClick={() => desligar(f)} className="p-1.5 rounded-md hover:bg-amber-100 text-warn" title="Desligar" data-testid={`func-desligar-${f.id}`}><UserX size={16} /></button>}
                    <button onClick={() => remove(f)} className="p-1.5 rounded-md hover:bg-red-100 text-danger" title="Excluir" data-testid={`func-delete-${f.id}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <h3 className="font-heading font-bold text-ink-900">{modal.mode === "new" ? "Novo funcionário" : "Editar funcionário"}</h3>
              <button onClick={close}><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4" data-testid="funcionario-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Nome completo" required value={form.nome} onChange={set("nome")} data-testid="func-nome" />
                <Input label="Cargo" value={form.cargo} onChange={set("cargo")} data-testid="func-cargo" />
                <Input label="CPF" value={form.cpf} onChange={set("cpf")} />
                <Input label="CTPS" value={form.ctps} onChange={set("ctps")} />
                <Input label="RG" value={form.rg} onChange={set("rg")} />
                <Input label="PIS" value={form.pis} onChange={set("pis")} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select label="Unidade" value={form.unidade} onChange={set("unidade")} data-testid="func-unidade">
                  <option value="Matriz VGS">Matriz VGS</option>
                  <option value="Araxá">Araxá</option>
                  <option value="Ituporanga">Ituporanga</option>
                </Select>
                <Select label="Tipo contrato" value={form.tipo_contrato} onChange={set("tipo_contrato")} data-testid="func-tipo">
                  <option value="CLT">CLT</option>
                  <option value="PJ">PJ</option>
                  <option value="Autonomo">Autônomo</option>
                  <option value="Diarista">Diarista</option>
                </Select>
                <Input label="Data admissão" type="date" value={form.data_admissao} onChange={set("data_admissao")} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {form.tipo_contrato === "Diarista" ? (
                  <Input label="Valor diária (R$)" type="number" step="0.01" value={form.valor_diaria} onChange={set("valor_diaria")} data-testid="func-diaria" />
                ) : (
                  <Input label="Salário base (R$)" type="number" step="0.01" value={form.salario_base} onChange={set("salario_base")} data-testid="func-salario" />
                )}
                <Input label="Dependentes IRRF" type="number" value={form.dependentes} onChange={set("dependentes")} data-testid="func-dep" />
                <Select label="Vinculado a vendedor" value={form.vinculado_vendedor} onChange={set("vinculado_vendedor")} data-testid="func-vend">
                  <option value="">— nenhum —</option>
                  <option value="Marquinhos">Marquinhos</option>
                  <option value="Boy">Boy</option>
                  <option value="Allan">Allan</option>
                  <option value="Matheus">Matheus</option>
                </Select>
              </div>
              {form.tipo_contrato === "CLT" && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      label="Complemento mensal “por fora” (R$)"
                      type="number" step="0.01"
                      value={form.complemento_mensal}
                      onChange={set("complemento_mensal")}
                      placeholder="0,00"
                      data-testid="func-complemento"
                    />
                    <div className="flex items-end">
                      {(Number(form.salario_base) > 0 || Number(form.complemento_mensal) > 0) && (
                        <div className="w-full px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-sm">
                          <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-emerald-800">Total real mensal</div>
                          <div className="font-mono font-bold text-emerald-900">
                            {fmtBRL((Number(form.salario_base) || 0) + (Number(form.complemento_mensal) || 0))}
                          </div>
                          <div className="text-[10px] text-emerald-700/80 leading-tight">
                            CTPS: {fmtBRL(Number(form.salario_base) || 0)} · Complemento: {fmtBRL(Number(form.complemento_mensal) || 0)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Overrides do cálculo padrão CLT (valor-hora e HE) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      label="Valor hora (R$/h) — sobrescreve salário/220"
                      type="number" step="0.01"
                      value={form.valor_hora}
                      onChange={set("valor_hora")}
                      placeholder="0 = calcular pelo salário"
                      data-testid="func-valor-hora"
                    />
                    <Input
                      label="Valor hora extra (R$/h) — sobrescreve CLT 50%"
                      type="number" step="0.01"
                      value={form.valor_hora_extra}
                      onChange={set("valor_hora_extra")}
                      placeholder="0 = valor_hora × 1,5"
                      data-testid="func-valor-he"
                    />
                  </div>
                  <div className="text-[11px] text-ink-500 -mt-1">
                    <strong>💡</strong> Preencha se o funcionário tem uma taxa fixa acordada (ex.: R$ 10,90/h e HE de R$ 15/h). Deixe 0 para calcular automaticamente pelo CLT.
                  </div>
                  <p className="text-[11px] text-ink-500 -mt-1">
                    O complemento <strong>não</strong> entra nos cálculos de INSS/IRRF/FGTS nem aparece no holerite oficial.
                    Ele será somado ao líquido e distribuído junto com o salário nas Contas a Pagar (respeitando o parcelamento abaixo, se ativo).
                  </p>

                  {/* INSS — % coberto pela empresa */}
                  <div className="p-3 rounded-md bg-stone-50 border border-ink-200 flex items-center gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <label className="text-xs font-semibold text-ink-900">Empresa cobre parte do INSS do funcionário</label>
                      <p className="text-[11px] text-ink-500 mt-0.5">
                        % do INSS que <strong>a empresa banca no lugar do funcionário</strong>. Ex.: <strong>50%</strong> = a empresa arca com metade do INSS do empregado (o funcionário só desconta a outra metade no holerite).
                        <br /><em>O INSS total continua sendo recolhido normalmente para a Receita.</em>
                      </p>
                    </div>
                    <div className="w-24">
                      <Input
                        label="% empresa"
                        type="number" min="0" max="100" step="0.5"
                        value={form.inss_empresa_pct}
                        onChange={set("inss_empresa_pct")}
                        data-testid="func-inss-pct"
                      />
                    </div>
                  </div>

                  {/* Parcelamento do salário (vale + saldo) */}
                  <div className="p-3 rounded-md bg-stone-50 border border-ink-200">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!form.salario_parcelado}
                        onChange={(e) => setForm((s) => ({ ...s, salario_parcelado: e.target.checked }))}
                        className="w-4 h-4 accent-brand-500 cursor-pointer"
                        data-testid="func-parcelado"
                      />
                      <span className="text-sm font-medium text-ink-900">Dividir salário em 2 parcelas (vale + saldo)</span>
                    </label>
                    {form.salario_parcelado && (
                      <div className="mt-3 grid grid-cols-2 gap-3 items-end">
                        <Input
                          label="Dia do vale (adiantamento)"
                          type="number" min="1" max="28"
                          value={form.dia_adiantamento}
                          onChange={set("dia_adiantamento")}
                          data-testid="func-dia-vale"
                        />
                        <div className="text-xs text-ink-500 pb-2">
                          Vale: dia <strong>{form.dia_adiantamento || 16}</strong> do mês da competência<br />
                          Saldo: <strong>5º dia útil</strong> do mês seguinte
                        </div>
                        {(Number(form.salario_base) > 0 || Number(form.complemento_mensal) > 0) && (
                          <div className="col-span-2 grid grid-cols-2 gap-2 text-xs">
                            <div className="px-3 py-2 rounded-md bg-white border border-ink-200">
                              <div className="text-[10px] text-ink-500 uppercase">Vale (50%)</div>
                              <div className="font-mono font-bold text-brand-500">
                                {fmtBRL(((Number(form.salario_base) || 0) + (Number(form.complemento_mensal) || 0)) / 2)}
                              </div>
                            </div>
                            <div className="px-3 py-2 rounded-md bg-white border border-ink-200">
                              <div className="text-[10px] text-ink-500 uppercase">Saldo (50%)</div>
                              <div className="font-mono font-bold text-brand-500">
                                {fmtBRL(((Number(form.salario_base) || 0) + (Number(form.complemento_mensal) || 0)) / 2)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input label="Vale-transporte (R$)" type="number" step="0.01" value={form.vale_transporte} onChange={set("vale_transporte")} />
                    <Input label="Vale-alimentação (R$)" type="number" step="0.01" value={form.vale_alimentacao} onChange={set("vale_alimentacao")} />
                  </div>
                </>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input label="E-mail" type="email" value={form.email} onChange={set("email")} />
                <Input label="Telefone" value={form.telefone} onChange={set("telefone")} />
                <Input label="Chave PIX" value={form.chave_pix} onChange={set("chave_pix")} />
              </div>
              {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={close}>Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid="func-save-btn">{busy ? <Spinner size={16} /> : "Salvar"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
